/**
 * CardsPlantEnumList Component
 * 
 * Displays a comprehensive plant enum card with detailed information including:
 * - Plant image/icon
 * - Name and scientific name
 * - Category badge
 * - Growth time ranges for different stages
 * - Color, flavor, and allergen tags
 * - Edible status indicator
 * - Action buttons (view, edit, delete)
 * 
 * @param {Object} plantEnum - The plant enum data object
 * @param {boolean} isSmall - Whether to display in small (2-column) or large (1-column) layout
 */
import React, { useState } from "react";

import { firebaseStore } from "../../firebase/firestore";
import { deleteDoc, doc } from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { firebaseStorage } from "../../firebase/storage";
import { 
  IconButton, 
  Snackbar, 
  Paper, 
  Chip, 
  Tooltip,
  Box,
  Typography,
  Divider
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";

import { ImageLoader } from "../img/ImageLoader";
import { plantCategoryEnums } from "../../utils/enums";
import EditPlantEnum from "../../../views/owner/plantEnums/components/EditPlantEnum";

import "./UiCards.css";

function CardsPlantEnumList({ plantEnum, isSmall }: any) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  
  const style = {
    paper: {
      width: isSmall ? "49%" : "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
      marginTop: "20px",
      padding: "16px",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(68, 169, 154, 0.2)",
        transform: "translateY(-2px)"
      }
    },
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  /**
   * Checks if a storage reference exists by attempting to get its download URL
   * @param {any} imageRef - Firebase storage reference
   * @returns {Promise<boolean>} Whether the image exists
   */
  const checkIfLogoExists = async (imageRef: any) => {
    try {
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  /**
   * Deletes the plant enum and all associated images from Firestore and Storage
   * Handles cleanup of:
   * - Icon image
   * - Main image
   * - Image array (multiple images)
   * - Firestore document
   */
  const handleDelete = async () => {
    try {
      setIsDialogOpen(false);
      // Check if icon is not '' and delete it from storage
      if (plantEnum.icon !== "") {
        const iconPathString = `cloud/plantEnums/${plantEnum.id}/${plantEnum.id}-icon.png`;
        const storageRef = ref(firebaseStorage, iconPathString);
        if (await checkIfLogoExists(storageRef)) {
          await deleteObject(storageRef);
        }
      }
      // Check if image is not '' and delete it from storage
      if (plantEnum.image !== "") {
        const imagePathString = `cloud/plantEnums/${plantEnum.id}/${plantEnum.id}.jpeg`;
        const storageRef = ref(firebaseStorage, imagePathString);
        if (await checkIfLogoExists(storageRef)) {
          await deleteObject(storageRef);
        }
      }
      // Check if images is not [] and delete them from storage
      if (plantEnum.images.length > 0) {
        plantEnum.images.map(async (image: string, index: number) => {
          const imagePathString = `cloud/plantEnums/${plantEnum.id}/${
            plantEnum.id
          }-${index + 1}.jpeg`;
          const storageRef = ref(firebaseStorage, imagePathString);
          if (await checkIfLogoExists(storageRef)) {
            await deleteObject(storageRef);
          }
        });
      }
      await deleteDoc(doc(firebaseStore, "plantEnums", plantEnum.id));
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing document: ", error);
    }
  };

  /**
   * Formats grow time range for display
   * @param {number} min - Minimum days
   * @param {number} max - Maximum days
   * @returns {string} Formatted range string
   */
  const formatGrowTime = (min: number, max: number) => {
    return `${min}-${max} days`;
  };

  /**
   * Gets the category display name from the category value
   */
  const getCategoryName = () => {
    return plantCategoryEnums.find(
      (element) => element.value === plantEnum.category
    )?.name || "Unknown Category";
  };

  return (
    <Paper sx={style.paper} elevation={2}>
      {/* Header Section with Image and Title */}
      <Box display="flex" gap={2} mb={2}>
        {/* Plant Image */}
        <Box 
          sx={{ 
            width: "120px", 
            height: "120px", 
            flexShrink: 0,
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #e0e0e0"
          }}
        >
          <ImageLoader
            imgUrl={plantEnum.image === "" ? plantEnum.icon : plantEnum.image}
            radiusStyle={"8px"}
          />
        </Box>

        {/* Title and Category Section */}
        <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between">
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: "#39393c",
                mb: 0.5,
                fontSize: "1.1rem"
              }}
            >
              {plantEnum.name}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: "italic", 
                color: "#666",
                mb: 1
              }}
            >
              {plantEnum.scientificName}
            </Typography>
            <Chip 
              label={getCategoryName()} 
              size="small" 
              sx={{ 
                backgroundColor: "#44a99a",
                color: "#fff",
                fontWeight: 500
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1} mt={1}>
            <Tooltip title="View Details">
              <IconButton color="inherit" size="small">
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setIsDialogOpen(true)}
              >
                <ModeEditOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton 
                sx={{ color: "error.main" }} 
                size="small" 
                onClick={handleDelete}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Subtitle */}
      {plantEnum.subtitle && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: "#555",
            mb: 2,
            lineHeight: 1.5
          }}
        >
          {plantEnum.subtitle}
        </Typography>
      )}

      {/* Growth Times Section */}
      <Box mb={2}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            color: "#44a99a", 
            fontWeight: 600,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}
        >
          <AccessTimeIcon fontSize="small" /> Growth Timeline
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip 
            label={`Baby: ${formatGrowTime(plantEnum.growTimeBaby?.min || 0, plantEnum.growTimeBaby?.max || 0)}`}
            size="small"
            variant="outlined"
            sx={{ color: "#555" }}
          />
          <Chip 
            label={`Vegetative: ${formatGrowTime(plantEnum.growTimeVegetative?.min || 0, plantEnum.growTimeVegetative?.max || 0)}`}
            size="small"
            variant="outlined"
            sx={{ color: "#555" }}
          />
          <Chip 
            label={`Flowering: ${formatGrowTime(plantEnum.growTimeFlowering?.min || 0, plantEnum.growTimeFlowering?.max || 0)}`}
            size="small"
            variant="outlined"
            sx={{ color: "#555" }}
          />
        </Box>
      </Box>

      {/* Characteristics Section */}
      <Box mb={2}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            color: "#44a99a", 
            fontWeight: 600,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}
        >
          <LocalFloristIcon fontSize="small" /> Characteristics
        </Typography>
        
        {/* Colors */}
        {plantEnum.color && plantEnum.color.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" sx={{ color: "#666", mr: 1 }}>
              Colors:
            </Typography>
            {plantEnum.color.slice(0, 3).map((color: string, index: number) => (
              <Chip
                key={index}
                label={color}
                size="small"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  backgroundColor: "#f0f0f0",
                  fontSize: "0.75rem"
                }}
              />
            ))}
            {plantEnum.color.length > 3 && (
              <Chip
                label={`+${plantEnum.color.length - 3} more`}
                size="small"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  backgroundColor: "#e0e0e0",
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
        )}

        {/* Flavours */}
        {plantEnum.flavour && plantEnum.flavour.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" sx={{ color: "#666", mr: 1 }}>
              Flavours:
            </Typography>
            {plantEnum.flavour.slice(0, 3).map((flavour: string, index: number) => (
              <Chip
                key={index}
                label={flavour}
                size="small"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  backgroundColor: "#fff3e0",
                  fontSize: "0.75rem"
                }}
              />
            ))}
            {plantEnum.flavour.length > 3 && (
              <Chip
                label={`+${plantEnum.flavour.length - 3} more`}
                size="small"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  backgroundColor: "#ffe0b2",
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
        )}

        {/* Allergens */}
        {plantEnum.allergeens && plantEnum.allergeens.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" sx={{ color: "#d32f2f", mr: 1 }}>
              Allergens:
            </Typography>
            {plantEnum.allergeens.map((allergen: string, index: number) => (
              <Chip
                key={index}
                label={allergen}
                size="small"
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  backgroundColor: "#ffebee",
                  color: "#d32f2f",
                  fontSize: "0.75rem"
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Additional Info Section */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        {/* Edible Status */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {plantEnum.isEdible ? (
            <>
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "1rem" }} />
              <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 500 }}>
                Edible
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ color: "#f44336", fontSize: "1rem" }} />
              <Typography variant="caption" sx={{ color: "#f44336", fontWeight: 500 }}>
                Not Edible
              </Typography>
            </>
          )}
        </Box>

        {/* Nutrients Count */}
        {plantEnum.nutrients && plantEnum.nutrients.length > 0 && (
          <Typography variant="caption" sx={{ color: "#666" }}>
            {plantEnum.nutrients.length} Nutrients
          </Typography>
        )}

        {/* Kitchen Uses Count */}
        {plantEnum.kitchen && plantEnum.kitchen.length > 0 && (
          <Typography variant="caption" sx={{ color: "#666" }}>
            {plantEnum.kitchen.length} Kitchen Uses
          </Typography>
        )}
      </Box>

      {/* Snackbar for Delete Confirmation */}
      <Snackbar
        open={isSnackbarOpen}
        message={`${plantEnum.name} deleted successfully`}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={5000}
        onClose={() => setIsSnackbarOpen(false)}
      />

      {/* Edit Dialog */}
      {isDialogOpen && (
        <EditPlantEnum
          plantEnumInitData={plantEnum}
          isDialogOpen={isDialogOpen}
          handleClose={handleClose}
        />
      )}
    </Paper>
  );
}

export default CardsPlantEnumList;
